/**
 * SmallMultiplesGrid - Paired horizontal bars in a grid (Tufte small multiples).
 *
 * 3x2 grid of metric comparisons. Each cell shows two thin bars (away/home) with values.
 * Sport-agnostic: accepts array of {label, homeValue, awayValue, max, format}.
 *
 * @param {Array} metrics - [{label, homeValue, awayValue, max, format}]
 * @param {Object} homeTeam - {abbreviation}
 * @param {Object} awayTeam - {abbreviation}
 */
import Card from '../../common/Card';

const defaultFormat = (v) => v != null ? `${v}` : '-';

const MetricCell = ({ label, homeValue, awayValue, max, format }) => {
  const formatter = format || defaultFormat;
  const maxVal = max || Math.max(homeValue || 0, awayValue || 0, 1);

  const homeWidth = homeValue != null ? Math.min(100, (homeValue / maxVal) * 100) : 0;
  const awayWidth = awayValue != null ? Math.min(100, (awayValue / maxVal) * 100) : 0;

  // Determine who's better
  const homeBetter = homeValue != null && awayValue != null && homeValue > awayValue;
  const awayBetter = awayValue != null && homeValue != null && awayValue > homeValue;

  return (
    <div className="space-y-0.5">
      <div className="text-[9px] text-text-secondary font-medium">{label}</div>

      {/* Away bar */}
      <div className="flex items-center gap-1">
        <div className="flex-1 h-2 bg-border/20 rounded-sm overflow-hidden">
          <div
            className={`h-full rounded-sm transition-all ${awayBetter ? 'bg-purple-400' : 'bg-purple-400/40'}`}
            style={{ width: `${awayWidth}%` }}
          />
        </div>
        <span className={`font-mono text-[9px] w-8 text-right ${
          awayBetter ? 'text-purple-400 font-semibold' : 'text-text-secondary'
        }`}>
          {formatter(awayValue)}
        </span>
      </div>

      {/* Home bar */}
      <div className="flex items-center gap-1">
        <div className="flex-1 h-2 bg-border/20 rounded-sm overflow-hidden">
          <div
            className={`h-full rounded-sm transition-all ${homeBetter ? 'bg-accent-blue' : 'bg-accent-blue/40'}`}
            style={{ width: `${homeWidth}%` }}
          />
        </div>
        <span className={`font-mono text-[9px] w-8 text-right ${
          homeBetter ? 'text-accent-blue font-semibold' : 'text-text-secondary'
        }`}>
          {formatter(homeValue)}
        </span>
      </div>
    </div>
  );
};

const SmallMultiplesGrid = ({
  metrics = [],
  homeTeam = {},
  awayTeam = {},
}) => {
  if (metrics.length === 0) return null;

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
          Advanced Metrics
        </h3>
        <div className="flex items-center gap-2 text-[9px]">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-purple-400"></span>
            {awayTeam.abbreviation || 'Away'}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-accent-blue"></span>
            {homeTeam.abbreviation || 'Home'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {metrics.map((metric, i) => (
          <MetricCell key={i} {...metric} />
        ))}
      </div>
    </Card>
  );
};

export default SmallMultiplesGrid;
